import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp, AlertCircle, CheckCircle2, AlertTriangle, XCircle, Clock, Zap, ShieldAlert, Eye, ArrowRight } from 'lucide-react';
import { acknowledgeConstraintEvaluation } from '../../lib/planningService';
import { OverridePolicyModal } from './OverridePolicyModal';
import { isSevereViolation, SEVERE_THRESHOLD } from '../../lib/constraintViolations';
import type { ConstraintViolation } from '../../lib/constraintViolations';
import { normalizeCheckKey, CHECK_FAILURE_LABELS as CHECK_LABELS } from '../../lib/planningConstraintKeys';

/** Sub-page (under the plan base path) where each check is actually fixed. */
const CHECK_FIX_PATHS: Record<string, string> = {
  DependenciesSatisfied: '/work-graph',
  DependencyCycle: '/work-graph',
  RecipePending: '/work-graph',
  BucketsMaterialized: '/capacity',
  CapacityFeasible: '/capacity',
  CapacityOverrun: '/capacity',
  NoCapacityModel: '/capacity',
  AssumptionsValid: '/assumptions-risks',
  AssumptionInvalidated: '/assumptions-risks',
  MilestoneAtRisk: '/milestones',
};

// ─── Types ────────────────────────────────────────────────────────────────────


interface EvaluationSummary {
  pass?: number;
  warn?: number;
  fail?: number;
  total?: number;
}

interface EvaluationDetails {
  violations?: ConstraintViolation[];
  [key: string]: unknown;
}

export interface PlanHealthPanelProps {
  evaluationStatus: string | null;
  evaluationSummary: EvaluationSummary | null;
  evaluationDetails: EvaluationDetails | null;
  evaluationComputedAt: string | null;
  evaluationId: string | null;
  planVersionId: string | null;
  openDivergenceCount: number;
  onRunEngine?: () => void;
  onEvaluationChanged?: () => void;
  /** Plan base path (e.g. /planning/plans/:id) — enables per-violation fix links. */
  planBase?: string | null;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function severityColor(severity: string | number | undefined, result: string | undefined): {
  bg: string; text: string; dot: string;
} {
  const s = typeof severity === 'string'
    ? severity.toLowerCase()
    : typeof severity === 'number'
      ? (severity >= SEVERE_THRESHOLD ? 'fail' : 'warn')
      : result?.toLowerCase();
  if (s === 'fail' || s === 'error') return { bg: 'bg-[var(--coral-dim)]', text: 'text-[var(--coral)]', dot: 'bg-[var(--coral)]' };
  if (s === 'warn' || s === 'warning') return { bg: 'bg-[var(--mustard-dim)]', text: 'text-[var(--mustard)]', dot: 'bg-[var(--mustard)]' };
  return { bg: 'bg-[var(--green-dim)]', text: 'text-[var(--green)] dark:text-[var(--text-ter)]', dot: 'bg-[var(--green)]' };
}

// ─── Component ────────────────────────────────────────────────────────────────

export function PlanHealthPanel({
  evaluationStatus,
  evaluationSummary,
  evaluationDetails,
  evaluationComputedAt,
  evaluationId,
  planVersionId,
  openDivergenceCount,
  onRunEngine,
  onEvaluationChanged,
  planBase,
}: PlanHealthPanelProps) {
  const [expanded, setExpanded] = useState(false);
  const [acknowledging, setAcknowledging] = useState(false);
  const [showOverrideModal, setShowOverrideModal] = useState(false);

  const pass = Number(evaluationSummary?.pass ?? 0);
  const warn = Number(evaluationSummary?.warn ?? 0);
  const fail = Number(evaluationSummary?.fail ?? 0);
  const total = Number(evaluationSummary?.total ?? (pass + warn + fail));
  const violations = evaluationDetails?.violations ?? [];
  const severeViolations = violations.filter(isSevereViolation);
  const hasCounts = pass > 0 || warn > 0 || fail > 0 || total > 0;

  const handleAcknowledge = async () => {
    if (!evaluationId || !planVersionId) return;
    setAcknowledging(true);
    await acknowledgeConstraintEvaluation(evaluationId, planVersionId);
    setAcknowledging(false);
    onEvaluationChanged?.();
  };

  // No evaluation yet
  if (!evaluationStatus || evaluationStatus === 'none') {
    return (
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] px-5 py-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-[var(--text-pri)] flex items-center gap-2">
            <Zap className="h-4 w-4 text-[var(--text-ter)]" />
            Plan Health
          </h2>
        </div>
        <div className="flex items-start gap-3 py-4 text-[var(--text-ter)]">
          <Clock className="h-4 w-4 flex-shrink-0 mt-0.5" />
          {/* Say what the check tests and what it is for — "run the engine to
              check constraints" named neither (owner, 2026-10-07). */}
          <div className="min-w-0">
            <p className="text-sm font-medium text-[var(--text-sec)]">Not checked yet.</p>
            <p className="text-sm mt-1">
              Checking tests this version of the plan: does the work fit the capacity, is any work blocked by a
              dependency, is anything missing an estimate. The plan cannot be authorized until it is checked and
              any blocking problem is fixed or overridden.
            </p>
          </div>
          {onRunEngine && (
            <button
              type="button"
              onClick={onRunEngine}
              className="ml-auto self-center px-3 py-1.5 bg-primary-600 text-white text-xs font-medium hover:bg-primary-700 flex-shrink-0"
            >
              Check plan health
            </button>
          )}
        </div>
      </div>
    );
  }

  // Pending evaluation
  if (evaluationStatus === 'pending') {
    return (
      <div className="bg-[var(--mustard-dim)] border border-[var(--mustard)] px-5 py-4">
        <div className="flex items-center gap-2 text-[var(--mustard)]">
          <Clock className="h-4 w-4" />
          <p className="text-sm font-medium">A check was started and has not finished — check again to refresh the results.</p>
          {onRunEngine && (
            <button
              type="button"
              onClick={onRunEngine}
              className="ml-auto px-3 py-1.5 bg-[var(--mustard)] text-white text-xs font-medium hover:bg-[var(--mustard)] flex-shrink-0"
            >
              Check plan health
            </button>
          )}
        </div>
      </div>
    );
  }

  const hasProblems = fail > 0 || warn > 0;

  return (
    <div className="bg-[var(--bg-raised)] border border-[var(--rule)] px-5 py-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-[var(--text-pri)] flex items-center gap-2">
          <Zap className="h-4 w-4 text-[var(--text-ter)]" />
          Plan Health
          {evaluationComputedAt && (
            <span className="text-xs font-normal text-[var(--text-ter)] ml-1">
              · {new Date(evaluationComputedAt).toLocaleDateString()}
            </span>
          )}
        </h2>
        {onRunEngine && (
          <button
            type="button"
            onClick={onRunEngine}
            className="text-xs text-primary-600 hover:text-primary-700 font-medium"
          >
            Check again
          </button>
        )}
      </div>

      {/* Summary stats — hide the 0/0/0 row when there are no counts (looks broken next to “all passed”) */}
      {hasCounts ? (
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5">
            <CheckCircle2 className="h-4 w-4 text-[var(--green)]" />
            <span className="text-sm font-medium text-[var(--green)] dark:text-[var(--text-ter)]">{pass}</span>
            <span className="text-xs text-[var(--text-ter)]">passed</span>
          </div>
          <div className="flex items-center gap-1.5">
            <AlertTriangle className="h-4 w-4 text-[var(--mustard)]" />
            <span className="text-sm font-medium text-[var(--mustard)]">{warn}</span>
            <span className="text-xs text-[var(--text-ter)]">warnings</span>
          </div>
          <div className="flex items-center gap-1.5">
            <XCircle className="h-4 w-4 text-[var(--coral)]" />
            <span className="text-sm font-medium text-[var(--coral)]">{fail}</span>
            <span className="text-xs text-[var(--text-ter)]">failures</span>
          </div>
          {total > 0 && (
            <span className="ml-auto text-xs text-[var(--text-ter)]">{total} checks total</span>
          )}
        </div>
      ) : !hasProblems ? (
        <p className="text-sm text-[var(--green)] dark:text-[var(--text-ter)] flex items-center gap-1.5">
          <CheckCircle2 className="h-4 w-4" />
          No constraint failures recorded for this evaluation.
        </p>
      ) : null}

      {/* Divergence callout */}
      {openDivergenceCount > 0 && (
        <div className="flex items-center gap-2 px-3 py-2 bg-[var(--mustard-dim)] border border-[var(--mustard)] text-sm text-[var(--mustard)]">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {openDivergenceCount} open rank divergence{openDivergenceCount !== 1 ? 's' : ''} — see Divergences below.
        </div>
      )}

      {/* Violation list (expandable) */}
      {hasProblems && violations.length > 0 && (
        <div>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="flex items-center gap-1.5 text-xs font-medium text-[var(--text-sec)] hover:text-[var(--text-pri)]"
          >
            {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            {expanded ? 'Hide' : 'Show'} {violations.length} violation{violations.length !== 1 ? 's' : ''}
          </button>
          {expanded && (
            <ul className="mt-2 space-y-2">
              {violations.map((v, idx) => {
                const colors = severityColor(v.severity, v.result);
                const canonicalKey = normalizeCheckKey(v.check_key);
                const label = CHECK_LABELS[canonicalKey] ?? v.check_key;
                const fixPath = CHECK_FIX_PATHS[canonicalKey];
                const severe = isSevereViolation(v);
                return (
                  <li key={`${v.check_key}-${idx}`} className={`${colors.bg} border  px-3 py-2.5 space-y-1`}>
                    <div className="flex items-center gap-2">
                      <span className={`w-1.5 h-1.5 rounded-full ${colors.dot} flex-shrink-0`} />
                      <span className={`text-sm font-medium ${colors.text}`}>{label}</span>
                      <span
                        className={`ml-auto text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded font-medium ${colors.bg} ${colors.text} border border-current/20`}
                        title={`check: ${v.check_key}${v.severity != null ? ` · severity ${v.severity}` : ''}`}
                      >
                        {severe ? 'blocking' : 'advisory'}
                      </span>
                    </div>
                    {v.explanation && (
                      <p className="text-xs text-[var(--text-sec)] pl-3.5">{v.explanation}</p>
                    )}
                    <div className="flex items-center justify-between pl-3.5 gap-2">
                      {v.remediation ? (
                        <p className="text-xs text-[var(--text-ter)] italic">{v.remediation}</p>
                      ) : (
                        <span />
                      )}
                      {planBase && fixPath && (
                        <Link
                          to={`${planBase}${fixPath}`}
                          className={`inline-flex items-center gap-1 text-xs font-medium ${colors.text} hover:underline flex-shrink-0`}
                        >
                          Fix it <ArrowRight className="h-3 w-3" />
                        </Link>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {!hasProblems && hasCounts && (
        <p className="text-sm text-[var(--green)] dark:text-[var(--text-ter)] flex items-center gap-1.5">
          <CheckCircle2 className="h-4 w-4" />
          All constraint checks passed.
        </p>
      )}

      {/* Governance action bar — shown for computed evaluations */}
      {evaluationStatus === 'computed' && evaluationId && planVersionId && (
        <div className="flex items-center gap-2 pt-1 border-t border-[var(--rule)]">
          {/* Acknowledge — marks planner has reviewed */}
          <button
            type="button"
            onClick={() => void handleAcknowledge()}
            disabled={acknowledging}
            title="Record that you have looked at these results. It changes nothing else."
            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[var(--rule)] text-xs font-medium text-[var(--text-sec)] hover:bg-[var(--surface)] disabled:opacity-50"
          >
            <Eye className="h-3.5 w-3.5" />
            {acknowledging ? 'Acknowledging…' : 'Acknowledge review'}
          </button>
          {/* Override Policy — required for severe violations before POR auth */}
          {severeViolations.length > 0 && (
            <button
              type="button"
              onClick={() => setShowOverrideModal(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[var(--coral-dim)] border border-[var(--coral)] text-xs font-medium text-[var(--coral)] hover:bg-[var(--coral-dim)]"
            >
              <ShieldAlert className="h-3.5 w-3.5" />
              Override policy ({severeViolations.length})
            </button>
          )}
        </div>
      )}

      {/* Override Policy modal */}
      {showOverrideModal && evaluationId && planVersionId && (
        <OverridePolicyModal
          planVersionId={planVersionId}
          evaluationId={evaluationId}
          violations={violations}
          onClose={() => setShowOverrideModal(false)}
          onOverridden={() => { setShowOverrideModal(false); onEvaluationChanged?.(); }}
        />
      )}
    </div>
  );
}
